import {AxisBottom, AxisRight} from '@visx/axis';
import {Grid} from '@visx/grid';
import {Group} from '@visx/group';
import {ScaleSVG} from '@visx/responsive';
import {scaleLinear, scaleTime} from '@visx/scale';
import {BarRounded} from '@visx/shape';
import {format, parseISO} from 'date-fns';

import {theme} from '#theme';

import {Tick} from './components';
import {
  getDataDrivenXScaleDomain,
  getDataDrivenXScaleTicks,
  getFallbackData,
} from './LifestyleTrendChart.util';

import {LifestyleTrendChartProps} from './LifestyleTrendChart.types';

import * as css from './LifestyleTrendChart.css';

// Offsets to reserve space for axis labels.
const leftOffset = 40;
const topOffset = 20;

/**
 * LifestyleTrendChart renders a chart depicting the user's lifestyle scores for all
 * Lifestyle Surveys they've taken over time. It uses a dynamic range of dates based
 * on the actual data range, with intelligently spaced tick marks that adapt
 * to the time span of the data (monthly, quarterly, semi-annually, or annually).
 *
 * The component is built using the visx charting library, which in turn uses
 * d3. For more information, review the docs for the relevant dependencies:
 *
 * - https://airbnb.io/visx/docs
 * - https://github.com/d3/d3/blob/main/API.md
 */
export const LifestyleTrendChart = ({
  data = [],
  height = 246,
  width = 520,
  ...otherProps
}: LifestyleTrendChartProps) => {
  const currentDate = new Date();
  const isEmpty = data.length <= 0;
  const renderedChartWidth = width - leftOffset;
  const renderedChartHeight = height - topOffset * 3;
  const renderedXAxisOffset = height - topOffset * 1.5;
  const xScale = scaleTime({
    domain: getDataDrivenXScaleDomain(data, currentDate),
    nice: true,
    range: [0, renderedChartWidth],
  });
  const xTickValues = getDataDrivenXScaleTicks(data, currentDate);
  const yScale = scaleLinear<number>({
    domain: [0, 100],
    nice: true,
    range: [renderedChartHeight, 0],
  });
  const yTickValues = [0, 50, 100];

  return (
    <ScaleSVG width={width} height={height} {...otherProps}>
      <defs>
        <linearGradient id="gradient" gradientTransform="rotate(90)">
          <stop offset="0%" stopColor="#EB4500" />
          <stop offset="19%" stopColor="#FF8570" />
          <stop offset="39%" stopColor="#FF9D80" />
          <stop offset="60%" stopColor="#FC7F7F" />
          <stop offset="80%" stopColor="#D7355A" />
          <stop offset="100%" stopColor="#731445" />
        </linearGradient>
      </defs>

      <Grid
        columnTickValues={[]}
        height={renderedChartHeight}
        rowTickValues={yTickValues}
        stroke={theme.colors.moonMist}
        top={topOffset}
        width={renderedChartWidth}
        xScale={xScale}
        yScale={yScale}
      />
      <Group top={topOffset}>
        <clipPath id="gradientBarsClipPath">
          {!isEmpty &&
            data.map(({registeredDate, lifestyleScorePercentage}, i) => {
              if (typeof lifestyleScorePercentage !== 'number') {
                return null;
              }

              const barHeight = yScale(100 - lifestyleScorePercentage);
              const barX = xScale(parseISO(registeredDate));
              const barY = yScale(0);

              return (
                <BarRounded
                  fill="#000"
                  height={barHeight}
                  key={i}
                  radius={4}
                  top
                  width={16}
                  x={barX - 8}
                  y={barY - barHeight}
                />
              );
            })}
        </clipPath>

        {(isEmpty ? getFallbackData(currentDate) : data).map(
          // Render submissions without a lifestyleScore or fallbacks
          // for an empty chart in outside of the clipPath.
          ({registeredDate, lifestyleScorePercentage}, i) => {
            if (!isEmpty && typeof lifestyleScorePercentage === 'number') {
              return null;
            }

            // If the chart is empty, show a single "ghost" bar with a score of
            // 100. Otherwise, submissions without a lifestyleScore should
            // appear as a "ghost" bar with a score of 80.
            const barHeight = yScale(100 - (lifestyleScorePercentage || 80));
            const barX = xScale(parseISO(registeredDate));
            const barY = yScale(0);

            return (
              <BarRounded
                fill="transparent"
                className={css.ghostBar}
                height={barHeight}
                key={i}
                radius={4}
                top
                width={16}
                x={barX - 8}
                y={barY - barHeight}
              />
            );
          },
        )}

        <rect
          clipPath="url(#gradientBarsClipPath)"
          fill="url('#gradient')"
          height={height}
          width={width}
        />
      </Group>

      <AxisBottom
        hideAxisLine
        hideTicks
        numTicks={4}
        labelOffset={0}
        scale={xScale}
        tickClassName={css.xTick}
        tickComponent={({formattedValue, ...otherProps}) => (
          <Tick
            formattedValue={formattedValue}
            isLast={formattedValue === format(new Date(xTickValues[0]), 'LLL ’yy')}
            {...otherProps}
          />
        )}
        tickFormat={date => format(date as Date, 'LLL ’yy')}
        tickValues={xTickValues}
        top={renderedXAxisOffset}
      />

      <AxisRight
        hideAxisLine
        hideTicks
        left={renderedChartWidth}
        scale={yScale}
        tickClassName={css.yTick}
        tickComponent={({formattedValue, dx, dy, x, y}) => (
          <text dx={dx} dy={dy} x={x} y={y}>
            {formattedValue}
          </text>
        )}
        tickValues={yTickValues}
        top={topOffset}
      />
    </ScaleSVG>
  );
};
